/**
 * THE ANSWER AND THE DECISION (C59, R47): what this browser holds, what the account holds, and what the two together
 * mean. Pure functions on strings and on what `GET /api/me` reports, so the tables of `consent.test.ts` can hold them.
 *
 * THE COOKIE, the landing site's contract (`landingpage/src/analytics/cookie.ts`):
 *
 *   gw_analytics=<g|d>.<text version>.<seconds since the epoch>
 *   Domain=groundwork-sports.com; Path=/; Max-Age=<13 months>; Secure; SameSite=Lax
 *
 * Not HttpOnly (only the page's own code reads it; the server never does), no `__Host-` prefix (that forbids
 * `Domain`). A visitor has no account, so the answer lives here and nowhere else (Q1).
 *
 * THE RECONCILIATION (analytics spec, *The consent*), at sign-in and on every read of `GET /api/me`. An answer to
 * another version of the text counts as no answer for that side (CS-D7), so an outdated yes loads nothing (Q10):
 *
 *   | In this browser | On the account          | Result                                                        |
 *   |-----------------|-------------------------|---------------------------------------------------------------|
 *   | none            | not asked               | ask                                                           |
 *   | none            | agreed                  | granted: the browser takes the agreement                      |
 *   | none            | declined or withdrawn   | declined: the browser takes the no                            |
 *   | agreed          | not asked               | granted: the account records the grant (client web_visitor)   |
 *   | declined        | not asked               | declined: the account records the decline (client web_visitor)|
 *   | agreed          | agreed                  | granted                                                       |
 *   | declined        | declined or withdrawn   | declined                                                      |
 *   | agreed          | declined or withdrawn   | conflict: nothing loads, nothing asks (CO-D4)                 |
 *   | declined        | agreed                  | conflict: nothing loads, nothing asks (CO-D4)                 |
 *
 * Without a session the browser's answer alone decides (none: ask; agreed: granted; declined: declined). With one,
 * nothing is decided until `GET /api/me` has answered (`pending`), so a conflict can never load first. A build whose
 * text is not the one the server holds in force asks nothing and loads nothing (`off`): an answer it recorded would
 * be to another text (consentRule.ts's guard, for the window of a deploy).
 */
import type { components } from '../api/generated/schema'
import { COOKIE_DOMAIN, COOKIE_MAX_AGE_SECONDS, COOKIE_NAME, offReason } from './config'

export type ConsentState = components['schemas']['ConsentStateOut']

/** `g` for "Accept all", `d` for "Only functional". */
export type Choice = 'g' | 'd'

export interface Answer {
  choice: Choice
  /** The version of the text the person was shown. */
  version: number
  /** When, in whole seconds since the epoch. */
  at: number
}

// ── The cookie ─────────────────────────────────────────────────────────────────────────────────────

const SHAPE = /^([gd])\.(\d{1,3})\.(\d{9,11})$/

/** An answer from a cookie's value, or null for anything that is not exactly the shape: a malformed value is none. */
export function parseAnswer(value: string | null | undefined): Answer | null {
  const found = SHAPE.exec(value ?? '')
  if (!found) return null
  const version = Number(found[2])
  if (version < 1) return null
  return { choice: found[1] as Choice, version, at: Number(found[3]) }
}

export function serializeAnswer(answer: Answer): string {
  return `${answer.choice}.${answer.version}.${answer.at}`
}

/** The value of one cookie in a `document.cookie` string. The last of two with one name wins, as a browser sends them. */
export function cookieValue(jar: string, name: string): string | undefined {
  let found: string | undefined
  for (const part of jar.split(';')) {
    const at = part.indexOf('=')
    if (at !== -1 && part.slice(0, at).trim() === name) found = part.slice(at + 1).trim()
  }
  return found
}

/** The answer in a cookie jar, whatever its version, or null. */
export function readAnswer(jar: string): Answer | null {
  return parseAnswer(cookieValue(jar, COOKIE_NAME))
}

/** What of `document` the cookie needs: a test hands in a jar of its own. */
export interface CookieJar {
  cookie: string
}

/**
 * Writes the answer and reads it back. True only if the jar now holds exactly this answer: a browser that keeps no
 * cookie leaves nothing to remember (AA-D9), and a visitor's yes that was not kept loads nothing.
 */
export function writeAnswer(doc: CookieJar, answer: Answer): boolean {
  doc.cookie = `${COOKIE_NAME}=${serializeAnswer(answer)}; Domain=${COOKIE_DOMAIN}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Secure; SameSite=Lax`
  const stored = readAnswer(doc.cookie)
  return stored !== null && serializeAnswer(stored) === serializeAnswer(answer)
}

/** Google Analytics' cookies: `_ga`, and `_ga_<stream id>` for each stream. */
const GOOGLE_COOKIE = /^_ga(?:_[A-Za-z0-9]+)?$/

export function googleCookieNames(jar: string): string[] {
  const names = new Set<string>()
  for (const part of jar.split(';')) {
    const at = part.indexOf('=')
    const name = (at === -1 ? part : part.slice(0, at)).trim()
    if (GOOGLE_COOKIE.test(name)) names.add(name)
  }
  return [...names].sort()
}

/**
 * Deletes every `_ga` and `_ga_*` cookie on the registrable domain, where the Google tag puts them ("the highest level
 * of domain possible"), and host-only, in case one was put there. A cookie is deleted by the (name, domain, path) it
 * was set with, so each pair is written expired. Returns the names it found.
 */
export function deleteGoogleCookies(doc: CookieJar): string[] {
  const names = googleCookieNames(doc.cookie)
  for (const name of names) {
    doc.cookie = `${name}=; Max-Age=0; Path=/; Domain=${COOKIE_DOMAIN}; Secure; SameSite=Lax`
    doc.cookie = `${name}=; Max-Age=0; Path=/; Secure; SameSite=Lax`
  }
  return names
}

// ── The reconciliation ─────────────────────────────────────────────────────────────────────────────

/** What the page knows of the account: no session, a session whose `me` has not answered, or what `me` reported. */
export type Account =
  | { kind: 'visitor' }
  | { kind: 'loading' }
  | { kind: 'ready'; state: ConsentState | undefined }

/** The account's answer to the text in force, with its date. */
export interface AccountAnswer {
  choice: Choice
  status: 'granted' | 'declined' | 'withdrawn'
  at: string | null
}

export type Mode = 'off' | 'pending' | 'ask' | 'granted' | 'declined' | 'conflict'

export interface Decision {
  mode: Mode
  /** Why it is off, for a reader and a test. */
  reason?: string
  /** On `ask`: either side holds an answer to an older text. That is an answer: nothing asks unprompted (CO-D4). */
  changed?: boolean
  /** On `conflict`: both answers and their dates. Nothing loads and nothing asks (CO-D4). */
  conflict?: { browser: { choice: Choice; at: number }; account: AccountAnswer }
  /** Rows 2 and 3: the cookie takes the account's answer. */
  browserTakes?: Answer
  /** Rows 4 and 5: the account records the browser's answer, with client `web_visitor` (AA-D6, ruling R1). */
  accountRecords?: Answer
}

/** The account's answer to the text in force; null for none (never asked, or an older text); `unknown` for a status this build does not know. */
export function accountAnswer(state: ConsentState): AccountAnswer | null | 'unknown' {
  const current = state.current_version
  const atLeast = (answered: number | null | undefined) => answered != null && answered >= current
  switch (state.status) {
    case 'unasked':
      return null
    case 'granted':
      return atLeast(state.version) ? { choice: 'g', status: 'granted', at: state.since ?? null } : null
    case 'declined':
      return atLeast(state.declined_version) ? { choice: 'd', status: 'declined', at: state.declined_at ?? null } : null
    case 'withdrawn':
      return atLeast(state.withdrawn_version) ? { choice: 'd', status: 'withdrawn', at: state.withdrawn_at ?? null } : null
    default:
      // A status this build has not heard of: nothing is guessed, nothing loads.
      return 'unknown'
  }
}

/** True when the account holds an answer, but to an older text than the one in force. */
function accountAnsweredOlder(state: ConsentState): boolean {
  const older = (answered: number | null | undefined) => answered != null && answered < state.current_version
  if (state.status === 'granted') return older(state.version)
  if (state.status === 'declined') return older(state.declined_version)
  if (state.status === 'withdrawn') return older(state.withdrawn_version)
  return false
}

/** An ISO date as whole seconds, or *fallback* for one that cannot be read. */
function seconds(iso: string | null, fallback: number): number {
  const ms = iso === null ? Number.NaN : Date.parse(iso)
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : fallback
}

/**
 * The table above, as a pure function.
 *
 * @param browser the answer in the cookie jar, whatever its version
 * @param account what the page knows of the account
 * @param shown the version of the text this build shows (`ANALYTICS_TEXT_VERSION`)
 * @param now the time in whole seconds, for an answer the cookie takes from an account that kept no date
 */
export function reconcile(browser: Answer | null, account: Account, shown: number, now: number): Decision {
  const given = browser !== null && browser.version === shown ? browser : null
  const browserOlder = browser !== null && browser.version < shown

  if (account.kind === 'visitor') {
    if (given === null) return { mode: 'ask', changed: browserOlder }
    return { mode: given.choice === 'g' ? 'granted' : 'declined' }
  }
  if (account.kind === 'loading') return { mode: 'pending' }

  const state = account.state
  if (state === undefined) return { mode: 'off', reason: 'no-analytics-consent' }
  if (state.current_version !== shown) return { mode: 'off', reason: 'other-text-in-force' }
  const held = accountAnswer(state)
  if (held === 'unknown') return { mode: 'off', reason: 'unknown-status' }

  if (given === null) {
    if (held === null) return { mode: 'ask', changed: browserOlder || accountAnsweredOlder(state) }
    const take: Answer = { choice: held.choice, version: shown, at: seconds(held.at, now) }
    return { mode: held.choice === 'g' ? 'granted' : 'declined', browserTakes: take }
  }
  if (held === null) return { mode: given.choice === 'g' ? 'granted' : 'declined', accountRecords: given }
  if (held.choice === given.choice) return { mode: given.choice === 'g' ? 'granted' : 'declined' }
  return { mode: 'conflict', conflict: { browser: { choice: given.choice, at: given.at }, account: held } }
}

/** The page's facts the decision reads. */
export interface DecisionInput {
  hostname: string
  containerId: string
  jar: string
  account: Account
  shown: number
  now: number
}

/** `off` on a page where analytics may not run at all; the reconciliation everywhere else. */
export function decide(input: DecisionInput): Decision {
  const reason = offReason(input.hostname, input.containerId)
  if (reason !== null) return { mode: 'off', reason }
  return reconcile(readAnswer(input.jar), input.account, input.shown, input.now)
}
